import { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Upload, ScanLine, CheckCircle2, AlertTriangle, Trash2, Loader2,
  FileText, Eye, RotateCw, HelpCircle, Download, Pencil,
  ArrowUp, ArrowDown, ArrowUpDown, ArrowUpAZ, ArrowDownAZ,
} from "lucide-react";
import { scanTaskBus } from "../lib/scanTaskBus";
import QuotaBar from "../components/QuotaBar";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import DocumentViewer from "../components/DocumentViewer";
import InvoiceLinePricing from "../components/InvoiceLinePricing";
import InvoiceLineEditDialog from "../components/InvoiceLineEditDialog";
import { reviewMeasurement, reviewPatch } from "../lib/invoiceReview";
import { toast } from "sonner";
import { api } from "../lib/api";
import { eur, pct, dateFR, unitLabel } from "../lib/format";

const TIER_LABELS = {
  confirmed:  { label: "Confirmé",   pill: "pill-down",  color: "#24845B", border: "#C0E1CC" },
  probable:   { label: "Probable",   pill: "pill-warn",  color: "#B36B00", border: "#F1D9A8" },
  to_verify:  { label: "À vérifier", pill: "pill-up",    color: "#D84343", border: "#F5C7C7" },
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [draft, setDraft] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [viewInvoice, setViewInvoice] = useState(null);   // full invoice with file_url + lines
  const [selectedLineIdx, setSelectedLineIdx] = useState(null);
  const fileInput = useRef();
  const cameraInput = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeInvoiceId } = useParams();  // /factures/:id[/verification]
  // Polling id (setInterval) actif tant qu'on attend un draft depuis
  // une tâche d'analyse en cours. Nettoyé à chaque changement de tâche.
  const pendingTaskRef = useRef(null);

  const load = async () => {
    const [inv, s, p] = await Promise.all([api.invoices(), api.suppliers(), api.products()]);
    setInvoices(inv); setSuppliers(s); setProducts(p);
  };
  useEffect(() => { load(); }, []);

  // Re-charge la liste dès qu'une tâche d'analyse aboutit (côté indicateur
  // global ou côté clic « Voir la facture »).
  useEffect(() => {
    const handler = () => { load().catch(() => {}); };
    window.addEventListener("rentascan:invoices-changed", handler);
    return () => window.removeEventListener("rentascan:invoices-changed", handler);
  }, []);

  // Ouverture directe par identifiant : /factures/:id[/verification] — la
  // facture persistée est chargée depuis le serveur (jamais depuis une
  // donnée temporaire). Si status=pending_review → panneau de vérification
  // (draft éditable), sinon fiche complète (viewInvoice).
  useEffect(() => {
    if (!routeInvoiceId) return;
    const wantsVerification = location.pathname.endsWith("/verification");
    let cancelled = false;
    (async () => {
      try {
        const inv = await api.getInvoice(routeInvoiceId);
        if (cancelled) return;
        if (inv.status === "pending_review") {
          setDraft({
            ...inv,
            __invoice_id: inv.id,
            supplier_name: inv.supplier_name || "",
            invoice_number: inv.invoice_number || "",
            invoice_date: inv.invoice_date || new Date().toISOString().slice(0, 10),
            total_ht: inv.total_ht || 0,
            total_ttc: inv.total_ttc,
            total_vat: inv.total_vat,
            file_url: inv.file_url || null,
            file_name: inv.file_name || null,
            file_mime: inv.file_mime || null,
            warnings: inv.warnings || [],
            pages: inv.pages || (inv.file_url ? [{ file_url: inv.file_url, file_name: inv.file_name, file_mime: inv.file_mime }] : []),
            page_count: (inv.pages || []).length || 1,
            lines: (inv.lines || []).map(normalizeExtractionLine),
          });
          setViewInvoice(null);
        } else {
          // Facture déjà validée / brouillon : jamais d'écran de vérification.
          // Si l'URL contient /verification, on la nettoie pour cohérence.
          if (wantsVerification) navigate(`/factures/${routeInvoiceId}`, { replace: true });
          setViewInvoice(inv);
          setDraft(null);
        }
      } catch (err) {
        if (cancelled) return;
        toast.error("Cette facture est introuvable ou n'a pas encore été enregistrée.", {
          description: "Vérification du statut en cours…",
        });
        // Retry after a short delay — the OCR task may still be finalising.
        setTimeout(async () => {
          try {
            const retry = await api.getInvoice(routeInvoiceId);
            if (retry.status === "pending_review") {
              navigate(`/factures/${routeInvoiceId}/verification`, { replace: true });
            } else {
              setViewInvoice(retry);
            }
          } catch { navigate("/factures", { replace: true }); }
        }, 2500);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeInvoiceId]);

  // Transforme une ligne d'extraction serveur en ligne éditable côté UI.
  // Extrait pour être réutilisé côté onFile (legacy sync) et côté ouverture
  // d'une tâche d'analyse asynchrone terminée (via ?task=xxx).
  const normalizeExtractionLine = (l, idx = 1) => ({
    // Preserve the calculation basis, source identifiers and quantity metadata.
    ...l,
    raw_text: l.raw_text || "",
    product_name: l.product_name || "",
    brand: l.brand || "",
    category: l.category || "",
    supplier_ref: l.supplier_ref || "",
    ean: l.ean || "",
    quantity: l.quantity || 0,
    unit: l.unit || "unité",
    pack_count: l.pack_count ?? null,
    units_per_pack: l.units_per_pack ?? null,
    total_units: l.total_units ?? null,
    content_value: l.content_value ?? null,
    content_unit: l.content_unit || null,
    total_volume_ml: l.total_volume_ml ?? null,
    total_weight_g: l.total_weight_g ?? null,
    price_per_unit: l.price_per_unit ?? null,
    price_per_l: l.price_per_l ?? null,
    price_per_cl: l.price_per_cl ?? null,
    price_per_kg: l.price_per_kg ?? null,
    unit_price_ht: l.unit_price_ht || 0,
    discount: l.discount || 0,
    line_total_ht: l.line_total_ht || 0,
    vat_rate_line: l.vat_rate_line || null,
    confidence: l.confidence ?? 0.5,
    status_tier: l.status_tier || (l.confidence >= 0.9 ? "confirmed" : l.confidence >= 0.7 ? "probable" : "to_verify"),
    method: l.method || null,
    warnings: l.warnings || [],
    matched_product_id: l.matched_product_id || null,
    ref_price_ht: l.ref_price_ht || null,
    variation_pct: l.variation_pct || null,
    page_index: l.page_index || idx,
  });

  // Construit un draft depuis une tâche d'analyse serveur terminée.
  const loadDraftFromTask = (task) => {
    const ex = task.extraction || {};
    const pages = task.pages || [];
    setDraft({
      ...ex,
      supplier_name: ex.supplier_name || "",
      invoice_number: ex.invoice_number || "",
      invoice_date: (ex.invoice_date || new Date().toISOString().slice(0, 10)),
      total_ht: ex.total_ht || 0,
      total_ttc: ex.total_ttc || null,
      total_vat: ex.total_vat || null,
      file_url: pages[0]?.file_url || null,
      file_name: pages[0]?.file_name || null,
      file_mime: pages[0]?.file_mime || null,
      warnings: ex.warnings || [],
      page_count: pages.length,
      pages: pages.map((p) => ({
        file_url: p.file_url, file_name: p.file_name,
        file_mime: p.file_mime, native_name: p.native_name || p.file_name,
      })),
      lines: (ex.lines || []).map(normalizeExtractionLine),
      __task_id: task.id,
    });
  };

  // Ouverture d'une facture analysée depuis la notification globale
  // (URL: /factures?task=xxxx). Poll jusqu'à ce que la tâche soit finie,
  // puis charge le brouillon. Nettoie l'URL une fois consommée.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get("task");
    if (!taskId) return;
    // Nettoie tout de suite l'URL pour éviter les re-triggers.
    navigate("/factures", { replace: true });

    let cancelled = false;
    const clear = () => { if (pendingTaskRef.current) { clearInterval(pendingTaskRef.current); pendingTaskRef.current = null; } };
    clear();

    const load = async () => {
      try {
        const task = await api.getScanTask(taskId);
        if (cancelled) return;
        if (task.status === "review_required" || task.status === "completed") {
          loadDraftFromTask(task);
          scanTaskBus.remove(taskId);
          try { await api.dismissScanTask(taskId); } catch { /* silent */ }
          clear();
        } else if (task.status === "failed") {
          toast.error("Impossible d'analyser la facture", { description: task.error || "" });
          scanTaskBus.remove(taskId);
          try { await api.dismissScanTask(taskId); } catch { /* silent */ }
          clear();
        }
        // queued / processing → on reste sur poll
      } catch {
        // Tâche introuvable → nettoyage
        scanTaskBus.remove(taskId);
        clear();
      }
    };
    load();
    pendingTaskRef.current = setInterval(load, 2000);
    return () => { cancelled = true; clear(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setScanning(true);
    // Feb 2026 §« 1 fichier = 1 facture » — Chaque fichier sélectionné est
    // désormais envoyé comme une facture indépendante (tâche OCR distincte,
    // ligne d'historique distincte, décompté séparément dans le quota).
    // Auparavant, un `POST /invoices/scan-async` unique agrégeait tous les
    // fichiers dans une SEULE facture multi-pages : impossible d'importer
    // 3 factures d'un coup et de les voir apparaître comme 3 factures.
    let ok = 0, ko = 0;
    for (const f of files) {
      try {
        const res = await api.scanInvoiceAsync([f]);
        if (res?.task_id) scanTaskBus.add(res.task_id);
        ok += 1;
      } catch (err) {
        ko += 1;
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "object"
          ? (detail.message || "Envoi impossible")
          : (detail || err?.message || "Envoi impossible");
        toast.error(`« ${f.name} » : envoi impossible`, { description: String(msg) });
      }
    }
    if (ok > 0) {
      toast.success(
        ok === 1 ? "Facture envoyée" : `${ok} factures envoyées`,
        { description: ok === 1
            ? "L'analyse continue en arrière-plan. Vous serez notifié dès qu'elle est terminée."
            : "Chaque facture est analysée séparément. Vous serez notifié à la fin de chacune.",
          duration: 4500,
        });
    }
    setScanning(false);
    if (fileInput.current) fileInput.current.value = "";
    if (cameraInput.current) cameraInput.current.value = "";
  };

  const startManual = () => {
    setDraft({
      supplier_name: "", invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10),
      total_ht: 0, file_url: null, warnings: [], lines: [emptyLine()],
    });
  };

  const emptyLine = () => ({
    raw_text: "", product_name: "", quantity: 0, unit: "unité",
    unit_price_ht: 0, line_total_ht: 0, discount: 0,
    confidence: 1, status_tier: "confirmed", warnings: [], matched_product_id: null,
  });

  const setLine = (i, patch) => {
    const lines = [...draft.lines]; lines[i] = { ...lines[i], ...patch };
    setDraft({ ...draft, lines });
  };
  const addLine = () => setDraft({ ...draft, lines: [...draft.lines, emptyLine()] });
  const rmLine = (i) => setDraft({ ...draft, lines: draft.lines.filter((_, k) => k !== i) });

  // Multi-page — remove a scanned page from the draft. Drops every line
  // that came from that page and renumbers remaining page indices.
  const removeDraftPage = (pageIdx) => {
    if (!draft?.pages?.length) return;
    const nextPages = draft.pages.filter((_, i) => i !== pageIdx);
    const kept = draft.lines
      .filter((l) => (l.page_index || 1) !== pageIdx + 1)
      .map((l) => {
        const p = l.page_index || 1;
        return p > pageIdx + 1 ? { ...l, page_index: p - 1 } : l;
      });
    setDraft({
      ...draft,
      pages: nextPages,
      page_count: nextPages.length,
      lines: kept,
      file_url: nextPages[0]?.file_url || null,
      file_name: nextPages[0]?.file_name || null,
      file_mime: nextPages[0]?.file_mime || null,
    });
    toast.success(`Page ${pageIdx + 1} retirée`);
  };

  const [validating, setValidating] = useState(false);
  // Suppression facture — état centralisé (utilisé par la ligne du tableau
  // ET le bouton dans la fenêtre « Voir la facture »). AlertDialog Radix
  // remplace `window.confirm` (bloqué par certaines webviews mobiles).
  const [pendingDelete, setPendingDelete] = useState(null);   // invoice ou null
  const [deleting, setDeleting] = useState(false);
  const askDelete = (invoice) => setPendingDelete(invoice);
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const inv = pendingDelete;
    setDeleting(true);
    try {
      await api.deleteInvoice(inv.id);
      toast.success("Facture supprimée");
      // Retire immédiatement la ligne du tableau + ferme la fenêtre
      // d'aperçu si elle affichait cette facture.
      setInvoices((prev) => prev.filter((x) => x.id !== inv.id));
      if (viewInvoice?.id === inv.id) setViewInvoice(null);
      setPendingDelete(null);
      // Recharge en arrière-plan pour rafraîchir statuts + éventuels
      // recalculs de prix produits (cascade backend).
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    } finally {
      setDeleting(false);
    }
  };
  // Tri du tableau « Historique des factures » — cf. agentic edit Feb 2026.
  // Chaque en-tête cliquable bascule asc/desc ; deux icônes distinctes
  // pour texte (A→Z / Z→A) et nombre/date (croissant / décroissant).
  const [sortKey, setSortKey] = useState("invoice_date");
  const [sortDir, setSortDir] = useState("desc");

  // Ordre des statuts pour un tri déterministe (Erreur > À vérifier >
  // Brouillon > Validée) — permet de remonter les factures à traiter.
  const STATUS_ORDER = { error: 0, pending_review: 1, draft: 2, validated: 3 };
  // Feb 2026 — Les factures « À vérifier » (et Erreur) restent TOUJOURS en
  // haut de la liste jusqu'à leur validation, quel que soit le tri actif.
  // Elles forment un « bucket » prioritaire (priority=0) trié entre elles
  // par date décroissante ; les autres factures suivent le tri utilisateur.
  const priorityOf = (i) => (i.status === "pending_review" || i.status === "error" ? 0 : 1);
  const sortedInvoices = useMemo(() => {
    const arr = [...invoices];
    const dir = sortDir === "asc" ? 1 : -1;
    const acc = (i) => {
      switch (sortKey) {
        case "invoice_date": return new Date(i.invoice_date || 0).getTime();
        case "supplier_name": return (i.supplier_name || "").toLocaleLowerCase("fr");
        case "invoice_number": return (i.invoice_number || "").toLocaleLowerCase("fr");
        case "lines_count": return i.lines?.length || 0;
        case "total_ht": return Number(i.total_ht || 0);
        case "status": return STATUS_ORDER[i.status || "draft"] ?? 9;
        default: return 0;
      }
    };
    return arr.sort((a, b) => {
      // 1) Bucket prioritaire : « À vérifier » / Erreur en premier, TOUJOURS.
      const pa = priorityOf(a), pb = priorityOf(b);
      if (pa !== pb) return pa - pb;
      // 2) À l'intérieur du bucket prioritaire : plus récente en premier
      //    (le tri utilisateur ne s'applique qu'aux factures traitées).
      if (pa === 0) {
        const da = new Date(a.invoice_date || 0).getTime();
        const db = new Date(b.invoice_date || 0).getTime();
        if (da !== db) return db - da;
        // Entre statuts prioritaires : Erreur avant À vérifier.
        const sa = STATUS_ORDER[a.status] ?? 9;
        const sb = STATUS_ORDER[b.status] ?? 9;
        if (sa !== sb) return sa - sb;
        return 0;
      }
      // 3) Bucket normal : tri utilisateur (colonne + direction).
      const va = acc(a), vb = acc(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [invoices, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "invoice_date" || key === "total_ht" || key === "lines_count" ? "desc" : "asc"); }
  };

  const validate = async () => {
    if (validating) return;
    // Feb 2026 §« gate extraction_completeness » — Le backend pose
    // `extraction_completeness.incomplete=true` quand la somme des lignes
    // ne réconcilie pas le total facturé (>5 c) OU quand l'inventaire
    // structurel PDF détecte des EAN/refs manquants dans l'OCR. On refuse
    // la validation côté UI pour forcer une correction manuelle avant
    // pollution du catalogue. Le backend refait la même vérification
    // (POST /invoices/{iid}/validate → 400 si toujours incomplète).
    const ec = draft.extraction_completeness || {};
    if (ec.incomplete) {
      const parts = [];
      if (ec.delta_vs_reported != null && Math.abs(ec.delta_vs_reported) > 0.05) {
        parts.push(`écart total : ${ec.delta_vs_reported > 0 ? "+" : ""}${ec.delta_vs_reported.toFixed(2)} € HT`);
      }
      const missing = Array.isArray(ec.missing_line_candidates) ? ec.missing_line_candidates.length : 0;
      if (missing > 0) parts.push(`${missing} ligne(s) présentes dans le PDF absentes du récap`);
      toast.error("Extraction incomplète — corrigez avant validation", {
        description: parts.join(" · ") || "Voir le bandeau au-dessus des lignes.",
        duration: 9000,
      });
      return;
    }
    const invalidLine = draft.lines.findIndex((line) => !line.ignored && line.line_kind !== "charge" && line.status_tier !== "zero_price"
      && Number(line.line_total_ht) !== 0 && !reviewMeasurement(line).valid);
    if (invalidLine >= 0) {
      toast.error(`Ligne ${invalidLine + 1} : vérifiez la quantité, l'unité et le total HT.`);
      setSelectedLineIdx(invalidLine);
      return;
    }
    setValidating(true);
    const validatingToastId = toast.loading("Validation des produits en cours…");
    try {
    const sup = suppliers.find((s) => s.name.toLowerCase() === (draft.supplier_name || "").toLowerCase());
    // === Feb 2026 · Fix « Valider les produits » ===
    // Le clic sur « Valider les produits » vaut confirmation explicite de
    // TOUTES les lignes affichées. Sans ce marquage, le backend rejetait
    // silencieusement les lignes OCR à confiance < 0,75 (skipped_lines
    // reason="low_confidence" / "unmatched") → toast trompeur « faute de
    // confirmation manuelle » sans qu'aucune UI de confirmation n'existe.
    // Exceptions légitimes conservées : lignes à 0 € (`status_tier=zero_price`
    // filtrées côté backend) et lignes explicitement ignorées.
    const confirmedLines = draft.lines.map((l) => {
      const isZero = (Number(l.unit_price_ht) === 0 && Number(l.line_total_ht) === 0)
        || l.status_tier === "zero_price";
      if (isZero || l.ignored || l.line_kind === "charge") return { ...l };
      // Feb 2026 §« Valider = choix humain explicite pour CHAQUE ligne » —
      // Le clic Valider vaut confirmation des valeurs physiques finales
      // visibles à l'écran (base, quantité, prix validé), même si
      // l'utilisateur n'a pas touché les champs. On les tamponne comme
      // `reviewed_base_unit_source='human'` afin que toute re-validation
      // future passe par l'early-return de `apply_reviewed_measurement`
      // et ne recompose PAS `total_units` depuis pc×upp OCR (bug qui
      // transformait 1000 serviettes en 4 pièces après un clic Valider).
      // reviewPatch snapshot les valeurs brutes dans `review_original`
      // pour rollback ; skip les lignes dont la mesure n'est PAS valide
      // (base/quantité/prix incohérents → l'utilisateur doit trancher).
      const measurement = reviewMeasurement(l);
      const stamp = measurement.valid
        ? reviewPatch(l, {
            reviewed_base_unit: measurement.base,
            reviewed_quantity: measurement.quantity,
            reviewed_price_ht: measurement.price,
          })
        : reviewPatch(l);
      return { ...l, ...stamp, user_confirmed: true, status_tier: "confirmed" };
    });
    const payload = {
      ...Object.fromEntries(Object.entries(draft).filter(([key]) => key.startsWith("supplier_"))),
      supplier_id: draft.supplier_id || sup?.id || null,
      supplier_name: draft.supplier_name,
      invoice_number: draft.invoice_number,
      invoice_date: draft.invoice_date,
      file_url: draft.file_url,
      file_name: draft.file_name,
      file_mime: draft.file_mime,
      status: "pending_review",
      total_ht: Number(draft.total_ht || 0),
      total_ttc: draft.total_ttc,
      total_vat: draft.total_vat,
      warnings: draft.warnings,
      lines: confirmedLines.map((l) => ({
        ...l,
        product_id: l.matched_product_id,
        quantity: Number(l.quantity),
        unit_price_ht: Number(l.unit_price_ht),
        line_total_ht: Number(l.line_total_ht),
        discount: Number(l.discount),
        confidence: Number(l.confidence),
      })),
    };
    // Cas facture déjà persistée (arrivé via /factures/:id/verification depuis
    // le flow OCR async) : on met à jour puis on valide sans recréer.
    let inv;
    if (draft.__invoice_id) {
      inv = await api.updateInvoice(draft.__invoice_id, { ...payload, id: draft.__invoice_id });
    } else {
      inv = await api.createInvoice(payload);
    }
    // Save mapping memory pour toutes les lignes confirmées (le clic Valider
    // vaut confirmation → alias mémorisé pour les prochaines factures).
    for (const l of confirmedLines) {
      if (l.matched_product_id && l.raw_text && draft.supplier_name && !l.ignored) {
        try { await api.saveLineMemory(draft.supplier_name, l.raw_text, l.matched_product_id); } catch {}
      }
    }
    const result = await api.validateInvoice(inv.id);
    // Dashboard Live — les pages Aujourd'hui / Fiches recettes / Dashboard
    // écoutent cet event pour se rafraîchir sans reload manuel.
    try {
      window.dispatchEvent(new CustomEvent("rentascan:invoice-validated", {
        detail: { invoice_id: inv.id, updates: result.updates || [], skipped: result.skipped_lines || [] },
      }));
    } catch {}
    const skipped = result.skipped_lines || [];
    const created = result.created_products || [];
    const updatesCount = (result.updates || []).length;
    const recipesCount = result.recipes_recomputed_count || 0;
    const parts = [];
    if (created.length) parts.push(`${created.length} produit${created.length > 1 ? "s" : ""} créé${created.length > 1 ? "s" : ""}`);
    if (updatesCount) parts.push(`${updatesCount} produit${updatesCount > 1 ? "s" : ""} actualisé${updatesCount > 1 ? "s" : ""}`);
    if (recipesCount) parts.push(`${recipesCount} fiche${recipesCount > 1 ? "s" : ""} recette${recipesCount > 1 ? "s" : ""} recalculée${recipesCount > 1 ? "s" : ""}`);
    const summaryText = parts.length ? parts.join(", ") : "aucun changement";
    toast.dismiss(validatingToastId);
    // Ne signale que les lignes RÉELLEMENT non comptabilisables :
    // - `zero_price` : prix à 0 € (comportement voulu, message explicatif)
    // - `price_conflict` : PU/total contradictoires → l'utilisateur doit trancher
    // - `quota_exceeded` : plan trop limité pour créer plus de produits
    const zeroCount = skipped.filter((s) => s.reason === "zero_price").length;
    const conflictCount = skipped.filter((s) => s.reason === "price_conflict").length;
    const quotaCount = skipped.filter((s) => s.reason === "quota_exceeded").length;
    if (conflictCount > 0 || quotaCount > 0) {
      const reasons = [];
      if (conflictCount) reasons.push(`${conflictCount} ligne(s) avec prix à vérifier`);
      if (quotaCount) reasons.push(`${quotaCount} ligne(s) au-delà du quota de votre formule`);
      toast.warning(`Facture partiellement validée · ${summaryText}`, {
        description: reasons.join(" · ") + ". Corrigez ces lignes puis relancez la validation.",
        duration: 9000,
      });
    } else if (zeroCount > 0) {
      toast.success(`Facture validée : ${summaryText}.`, {
        description: `${zeroCount} ligne${zeroCount > 1 ? "s" : ""} à 0 € non comptabilisée${zeroCount > 1 ? "s" : ""} (comportement normal).`,
        duration: 7000,
      });
    } else {
      toast.success(`Facture validée : ${summaryText}.`, { duration: 6000 });
    }
    setDraft(null); await load();
    // Nettoie l'URL /factures/:id/verification si on venait de là.
    if (routeInvoiceId) navigate("/factures", { replace: true });
    try { window.dispatchEvent(new CustomEvent("rentascan:invoices-changed", { detail: { source: "validate", invoice_id: inv.id } })); } catch {}
    } catch (e) {
      toast.dismiss(validatingToastId);
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || "Validation impossible");
      toast.error("Validation échouée", { description: String(msg) });
    } finally {
      setValidating(false);
    }
  };

  const groups = useMemo(() => {
    // Feb 2026 §« crash reopen METRO 18/08 » — Le reduce accédait à
    // `acc[t].push` avec `t = "charge"` (lignes frais/livraison sur facture
    // déjà validée), levant `Cannot read properties of undefined (reading
    // 'push')`. Classement robuste : toute tier non-standard → to_verify,
    // et bucket `charge` séparé pour l'affichage des frais.
    const empty = { confirmed: [], probable: [], to_verify: [], charge: [], zero_price: [] };
    if (!draft) return empty;
    return draft.lines.reduce((acc, l, i) => {
      let t = l.status_tier || "probable";
      // line_kind="charge" (frais/livraison/éco-part) prime sur le tier
      if (l.line_kind === "charge") t = "charge";
      if (!acc[t]) t = "to_verify";
      acc[t].push({ ...l, __idx: i });
      return acc;
    }, empty);
  }, [draft]);

  const reanalyze = async (id) => {
    toast.info("Réanalyse en cours…");
    try {
      const r = await api.reanalyzeInvoice(id);
      toast.success("Facture réanalysée");
      await load();
      // open the panel for re-verification
      const ex = r.extraction || {};
      setDraft({
        ...r.invoice,
        supplier_name: r.invoice.supplier_name || "",
        invoice_number: r.invoice.invoice_number || "",
        invoice_date: r.invoice.invoice_date || "",
        warnings: ex.warnings || [],
        lines: ex.lines || [],
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Échec réanalyse"); }
  };

  return (
    <div className="space-y-6">
      <PageHero
        eyebrow="RentaScan · OCR intelligent"
        title="Factures fournisseurs"
        subtitle={`${invoices.length} facture(s) enregistrée(s) · reconnaissance de produits abrégés, conversion liquide/pack automatique`}
        action={
          <div className="flex gap-2">
            <button data-testid="invoice-export-csv-btn" onClick={() => window.open(api.exportInvoicesCsvUrl(), "_blank")}
              className="h-11 px-4 rounded-lg border border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[13px] text-[#0B1F33] inline-flex items-center gap-1.5 font-semibold">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button data-testid="invoice-export-pdf-btn" onClick={() => window.open(api.exportInvoicesPdfUrl(), "_blank")}
              className="btn-orange !h-11">
              <Download className="w-4 h-4" /> Export comptable PDF
            </button>
          </div>
        }
      />

      <QuotaBar resource="invoices" label="Factures analysées" />

      {!draft && (
        <>
          <div className="rounded-xl bg-white border border-[#CDD7E3] p-5 flex items-center gap-4 flex-wrap" data-testid="invoice-scan-zone">
            <div className="w-14 h-14 rounded-xl bg-[#DCEBFF] flex items-center justify-center shrink-0">
              <ScanLine className="w-7 h-7 text-[#0B1F33]" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-heading font-bold text-[17px] text-[#0B1F33]">Scanner une facture</div>
              <div className="text-[13px] text-[#5E6875] mt-0.5">JPG · PNG · HEIC · PDF — RentaScan reconnaît les abréviations. Sur mobile, prenez une photo directement ou ajoutez plusieurs pages.</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="invoice-upload-btn" disabled={scanning} onClick={() => fileInput.current?.click()} className="btn-orange !h-11 disabled:opacity-70">
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {scanning ? "Envoi…" : "Choisir un fichier"}
              </button>
              <button data-testid="invoice-manual-btn" onClick={startManual}
                className="h-11 px-4 rounded-lg border border-[#0B1F33] bg-white hover:bg-[#F4F5EF] text-[13px] text-[#0B1F33] font-semibold">
                Saisir manuellement
              </button>
              {/* Native file picker — accepts multi-selection for multi-page invoices. HEIC support on iOS. */}
              <Input ref={fileInput} data-testid="invoice-file-input" type="file" multiple
                accept=".jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,image/*,application/pdf"
                onChange={onFile} className="hidden" />
              {/* Native camera on mobile — capture=environment => rear camera, multiple => can shoot several pages. */}
              <input ref={cameraInput} data-testid="invoice-camera-input" type="file" multiple
                accept="image/*" capture="environment"
                onChange={onFile} className="hidden" />
            </div>
          </div>

          <div className="rounded-xl bg-white border border-[#CDD7E3] overflow-hidden">
            <div className="p-4 border-b border-[#CDD7E3] flex items-center justify-between">
              <h3 className="section-title text-[16px]">Historique des factures</h3>
              <div className="text-[12px] text-[#5E6875]">Consultez, réanalysez ou téléchargez l'original</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full dense">
                <thead>
                  <tr>
                    <SortableTh label="Date" k="invoice_date" kind="num" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortableTh label="Fournisseur" k="supplier_name" kind="text" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortableTh label="Lignes" k="lines_count" kind="num" align="num" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortableTh label="Total HT" k="total_ht" kind="num" align="num" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortableTh label="Statut" k="status" kind="num" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedInvoices.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-[#5E6875] py-10">Aucune facture pour le moment.</td></tr>
                  )}
                  {sortedInvoices.map((i) => {
                    const s = i.status || "draft";
                    const label = s === "validated" ? "Validée" : s === "pending_review" ? "À vérifier" : s === "error" ? "Erreur" : "Brouillon";
                    const pillClass = s === "validated" ? "pill-down" : s === "pending_review" ? "pill-warn" : s === "error" ? "pill-up" : "pill-info";
                    return (
                      <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
                        <td>{dateFR(i.invoice_date)}</td>
                        <td className="font-semibold">{i.supplier_name || "—"}</td>
                        <td className="num">{i.lines?.length || 0}</td>
                        <td className="num">{eur(i.total_ht)}</td>
                        <td><span className={`pill ${pillClass}`}>{label}</span></td>
                        <td className="text-right">
                          <div className="flex gap-1 justify-end">
                            {(i.file_url || i.status === "pending_review") && (
                              <button data-testid={`invoice-view-${i.id}`}
                                onClick={() => {
                                  // Facture "à vérifier" → écran de vérification
                                  // complet (mêmes boutons Valider en haut/bas
                                  // qu'après une notif "Analyse terminée").
                                  // Facture validée / brouillon → aperçu simple.
                                  if (i.status === "pending_review") {
                                    navigate(`/factures/${i.id}/verification`);
                                  } else {
                                    setViewInvoice(i);
                                  }
                                }}
                                title={i.status === "pending_review" ? "Vérifier et valider" : "Consulter l'original"}
                                className="h-8 px-2 rounded-md border border-[#CDD7E3] hover:bg-[#F4F5EF] text-[11.5px] font-semibold text-[#0B1F33] inline-flex items-center gap-1">
                                <Eye className="w-3.5 h-3.5" /> {i.status === "pending_review" ? "Vérifier" : "Voir"}
                              </button>
                            )}
                            {i.file_name && (
                              <button data-testid={`invoice-reanalyze-${i.id}`} onClick={() => reanalyze(i.id)}
                                title="Relancer l'analyse"
                                className="h-8 px-2 rounded-md border border-[#CDD7E3] hover:bg-[#F4F5EF] text-[11.5px] font-semibold text-[#0B1F33] inline-flex items-center gap-1">
                                <RotateCw className="w-3.5 h-3.5" /> Réanalyser
                              </button>
                            )}
                            <button
                              data-testid={`invoice-delete-${i.id}`}
                              onClick={() => askDelete(i)}
                              title="Supprimer la facture"
                              className="h-8 px-2 rounded-md border border-[#CDD7E3] hover:border-[#D84343] hover:bg-[#FDECEC] hover:text-[#D84343] text-[11.5px] font-semibold text-[#0B1F33] inline-flex items-center gap-1 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Supprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ============ Verification panel — split view ============ */}
      {draft && (
        <div className="rounded-xl bg-white border border-[#CDD7E3] overflow-hidden" data-testid="invoice-verification-panel">
          {/* header */}
          <div className="p-5 border-b border-[#CDD7E3] flex flex-wrap items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#1769E0]" />
            <h3 className="font-heading font-bold text-[17px] text-[#0B1F33]">Vérification de la facture</h3>
            <Badge className="bg-[#FFF0D2] text-[#B36B00] border-[#F1D9A8] hover:bg-[#FFF0D2]">Confirmation requise</Badge>
            {groups.to_verify.length > 0 && (
              <Badge
                data-testid="invoice-pending-decisions-badge"
                className="bg-[#FDECEC] text-[#7C2A21] border-[#F5C7C7] hover:bg-[#FDECEC] inline-flex items-center gap-1.5"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                {groups.to_verify.length} ligne{groups.to_verify.length > 1 ? "s" : ""} à décider
              </Badge>
            )}
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={() => {
                // Feb 2026 §« Annuler ne rouvre plus la vérif » —
                // Sans nettoyer /factures/:id/verification dans l'URL,
                // l'effet useEffect([routeInvoiceId]) reste inactif au
                // prochain clic « Vérifier » sur la même ligne. On revient
                // à /factures pour que le bouton rouvre correctement le
                // panneau de vérification par la même URL.
                setDraft(null);
                if (routeInvoiceId) navigate("/factures", { replace: true });
              }}>Annuler</Button>
              <Button onClick={validate} disabled={validating} data-testid="invoice-validate-btn" className="btn-orange border-0 !h-10">
                {validating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                {validating ? "Validation en cours…" : "Valider les produits"}
              </Button>
            </div>
          </div>

          {/* invoice-level metadata + warnings */}
          <div className="p-5 border-b border-[#CDD7E3] grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div><Label>Fournisseur</Label><Input data-testid="invoice-draft-supplier" value={draft.supplier_name} onChange={(e) => setDraft({ ...draft, supplier_name: e.target.value, supplier_id: null })} /></div>
            <div><Label>N° facture</Label><Input value={draft.invoice_number} onChange={(e) => setDraft({ ...draft, invoice_number: e.target.value })} /></div>
            <div><Label>Date</Label><Input type="date" value={(draft.invoice_date || "").slice(0, 10)} onChange={(e) => setDraft({ ...draft, invoice_date: e.target.value })} /></div>
            <div><Label>Total HT</Label><Input type="number" step="0.01" value={draft.total_ht} onChange={(e) => setDraft({ ...draft, total_ht: e.target.value })} /></div>
          </div>

          {/* Feb 2026 §« bandeau completeness » — Diagnostic visible avant
              validation quand le backend a détecté extraction incomplète
              OU un écart total non réconcilié. L'utilisateur peut corriger
              (ajouter/supprimer une ligne, ajuster un total) puis relancer
              la validation ; la complétude est recalculée automatiquement
              côté backend. Aucun montant inventé pour rééquilibrer. */}
          {draft.extraction_completeness?.incomplete && (
            <div
              data-testid="invoice-completeness-banner"
              className="mx-5 mt-4 mb-1 p-4 rounded-lg border-2 border-[#F5C7C7] bg-[#FDECEC]"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#B33A2E] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-[14px] text-[#7C2A21]">
                    Extraction incomplète — validation bloquée
                  </div>
                  <div className="mt-1 text-[12.5px] text-[#7C2A21] space-y-0.5 font-mono-num">
                    {draft.extraction_completeness.reported_total_ht != null && (
                      <div>
                        Total facturé : <b>{Number(draft.extraction_completeness.reported_total_ht).toFixed(2)} € HT</b>
                        {" · "}Somme des lignes : <b>{Number(draft.extraction_completeness.computed_total_ht || 0).toFixed(2)} € HT</b>
                        {draft.extraction_completeness.delta_vs_reported != null && (
                          <> {" · "}Écart : <b className="text-[#B33A2E]">
                            {draft.extraction_completeness.delta_vs_reported > 0 ? "+" : ""}
                            {Number(draft.extraction_completeness.delta_vs_reported).toFixed(2)} € HT
                          </b></>
                        )}
                      </div>
                    )}
                    {draft.extraction_completeness.pdf_product_lines_detected != null && (
                      <div>
                        Lignes produits dans le PDF : <b>{draft.extraction_completeness.pdf_product_lines_detected}</b>
                        {" · "}Livrées par l'OCR : <b>{draft.extraction_completeness.ocr_product_lines}</b>
                      </div>
                    )}
                  </div>
                  {Array.isArray(draft.extraction_completeness.missing_line_candidates)
                    && draft.extraction_completeness.missing_line_candidates.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-[12px] font-semibold text-[#7C2A21]">
                        {draft.extraction_completeness.missing_line_candidates.length} ligne(s) présentes dans le PDF absentes du récap OCR
                      </summary>
                      <ul className="mt-2 pl-4 text-[11.5px] text-[#5E6875] font-mono-num space-y-0.5 max-h-40 overflow-y-auto">
                        {draft.extraction_completeness.missing_line_candidates.slice(0, 20).map((it, i) => (
                          <li key={i} className="break-all">
                            <span className="text-[#0B1F33] font-semibold">EAN {it.ean}</span>
                            {it.ref && <span> · réf {it.ref}</span>}
                            {it.raw && <span className="block text-[#5E6875]">{it.raw}</span>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                  <div className="mt-2 text-[11.5px] text-[#7C2A21]">
                    Ajoutez les lignes manquantes ci-dessous et/ou ajustez le total HT.
                    La validation restera bloquée tant que la somme ne réconcilie pas le total facturé (tolérance 5 c).
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Multi-page thumbnails — chaque vignette pointe vers un fichier scanné
              du brouillon avec une croix pour la retirer avant validation. */}
          {draft.pages?.length > 1 && (
            <div className="px-5 py-3 border-b border-[#CDD7E3] bg-[#F4F5EF]" data-testid="invoice-pages-strip">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#5E6875] mb-2">
                {draft.pages.length} page(s) scannée(s)
              </div>
              <div className="flex gap-2 flex-wrap">
                {draft.pages.map((pg, idx) => {
                  const linesOnPage = draft.lines.filter((l) => (l.page_index || 1) === idx + 1).length;
                  const isImage = (pg.file_mime || "").startsWith("image/");
                  return (
                    <div key={idx} className="relative group w-[92px] shrink-0" data-testid={`invoice-page-thumb-${idx}`}>
                      <div className="w-full h-[120px] rounded-md border border-[#CDD7E3] bg-white overflow-hidden flex items-center justify-center">
                        {isImage ? (
                          <img src={pg.file_url} alt={`Page ${idx + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-center px-1.5">
                            <FileText className="w-6 h-6 text-[#1769E0] mx-auto" />
                            <div className="mt-1 text-[10px] font-bold text-[#0B1F33] truncate">{pg.native_name || pg.file_name}</div>
                          </div>
                        )}
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-1">
                        <span className="text-[10.5px] font-bold text-[#0B1F33]">P.{idx + 1}</span>
                        <span className="text-[10.5px] text-[#5E6875] font-mono-num">{linesOnPage} lignes</span>
                      </div>
                      <button
                        type="button"
                        aria-label={`Retirer la page ${idx + 1}`}
                        data-testid={`invoice-page-remove-${idx}`}
                        onClick={() => removeDraftPage(idx)}
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-[#CDD7E3] shadow-[0_2px_6px_rgba(11,31,51,0.15)] flex items-center justify-center hover:border-[#D84343] hover:text-[#D84343] transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  data-testid="invoice-add-page-btn"
                  className="w-[92px] h-[120px] rounded-md border-2 border-dashed border-[#CDD7E3] text-[#5E6875] hover:border-[#1769E0] hover:text-[#1769E0] flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors"
                >
                  <Upload className="w-4 h-4" /> Ajouter
                </button>
              </div>
            </div>
          )}

          {/* Split view: doc left, tri-status right */}
          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
            {/* LEFT — document viewer */}
            <div className="border-r border-[#CDD7E3] bg-[#F4F5EF] min-h-[600px] relative">
              {draft.file_url ? (
                <DocumentViewer src={draft.file_url} mime={draft.file_mime} name={draft.file_name || "Facture"} dense />
              ) : (
                <div className="h-[600px] flex items-center justify-center text-[13px] text-[#5E6875]">Saisie manuelle — pas de fichier original.</div>
              )}
            </div>

            {/* RIGHT — tri-status lines */}
            <div className="min-w-0">
              {["to_verify", "probable", "confirmed"].map((tier) => {
                const list = groups[tier];
                if (list.length === 0) return null;
                const meta = TIER_LABELS[tier];
                return (
                  <div key={tier} className="border-b border-[#CDD7E3] last:border-b-0">
                    <div className="px-4 py-2.5 flex items-center gap-2 bg-[#F4F5EF]">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
                        <span className="text-[13px] font-bold uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                      </span>
                      <span className="text-[12px] text-[#5E6875]">· {list.length} ligne(s)</span>
                    </div>
                    <div>
                      {list.map((l) => (
                        <LineRow key={l.__idx} l={l} idx={l.__idx} products={products}
                          selected={selectedLineIdx === l.__idx}
                          onSelect={() => setSelectedLineIdx(l.__idx)}
                          onChange={(patch) => setLine(l.__idx, patch)}
                          onRemove={() => rmLine(l.__idx)} />
                      ))}
                    </div>
                  </div>
                );
              })}
              <div className="p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-between border-t border-[#CDD7E3] bg-[#F4F5EF]">
                <Button variant="outline" onClick={addLine} className="w-full sm:w-auto" data-testid="invoice-add-line-bottom">
                  + Ajouter une ligne
                </Button>
                <div className="text-[11px] text-[#5E6875] flex items-center gap-1">
                  <HelpCircle className="w-3.5 h-3.5" />
                  Les prix ne sont jamais appliqués tant que vous ne validez pas.
                </div>
                <Button
                  onClick={validate}
                  disabled={validating}
                  data-testid="invoice-validate-btn-bottom"
                  className="btn-orange border-0 !h-10 w-full sm:w-auto"
                >
                  {validating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  {validating ? "Validation en cours…" : "Valider les produits"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integrated invoice viewer — plein écran mobile / split desktop.
          Feb 2026 : sur téléphone, la fenêtre déborde et le bouton de
          fermeture devient inaccessible. Correctif : override de la
          position Radix + header sticky avec X toujours visible + footer
          d'actions ancré. */}
      <Dialog open={!!viewInvoice} onOpenChange={(v) => !v && setViewInvoice(null)}>
        <DialogContent
          data-testid="invoice-preview-dialog"
          className="p-0 gap-0 overflow-hidden border-0
            w-screen h-[100dvh] max-w-none top-0 left-0 translate-x-0 translate-y-0 rounded-none
            sm:top-[50%] sm:left-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%]
            sm:w-[95vw] sm:max-w-[1200px] sm:h-auto sm:max-h-[90vh] sm:rounded-lg sm:border
            flex flex-col"
          style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <DialogHeader className="sticky top-0 z-10 px-4 sm:px-5 py-3 sm:py-4 border-b border-[#CDD7E3] bg-[#F4F5EF]">
            <DialogTitle className="flex items-center gap-2 text-[14px] sm:text-[15px] pr-8">
              <FileText className="w-4 h-4 text-[#1769E0] shrink-0" />
              <span className="font-bold truncate">{viewInvoice?.supplier_name || "Fournisseur —"}</span>
              <span className="text-[#5E6875] font-normal truncate hidden sm:inline">· {viewInvoice?.invoice_number || "N° —"}</span>
              <span className="text-[11.5px] sm:text-[12px] text-[#5E6875] font-normal ml-auto whitespace-nowrap">{dateFR(viewInvoice?.invoice_date)}</span>
              {viewInvoice?.status && (
                <span className={`pill hidden sm:inline ${viewInvoice.status === "validated" ? "pill-down" : viewInvoice.status === "pending_review" ? "pill-warn" : "pill-info"}`}>
                  {viewInvoice.status === "validated" ? "Validée" : viewInvoice.status === "pending_review" ? "À vérifier" : "Brouillon"}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {/* Desktop side-by-side */}
            <div className="hidden lg:grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] max-h-[75vh]">
              <div className="border-r border-[#CDD7E3]">
                {viewInvoice?.file_url ? (
                  <DocumentViewer src={viewInvoice.file_url} mime={viewInvoice.file_mime} name={viewInvoice.file_name || "Facture"} />
                ) : (
                  <div className="h-full flex items-center justify-center text-[13px] text-[#5E6875] p-10">Saisie manuelle — pas de fichier original.</div>
                )}
              </div>
              <InvoiceAnalysis
                invoice={viewInvoice}
                products={products}
                onLineSaved={async () => {
                  // Feb 2026 §« modale Voir facture doit refléter le save »
                  try {
                    const fresh = await api.getInvoice(viewInvoice.id);
                    if (fresh) setViewInvoice(fresh);
                  } catch { /* silent */ }
                  load();
                }}
              />
            </div>

            {/* Mobile tabs */}
            <div className="lg:hidden">
              <Tabs defaultValue="doc" className="w-full">
                <TabsList className="mx-4 mt-2 w-[calc(100%-32px)] grid grid-cols-2">
                  <TabsTrigger value="doc" data-testid="mobile-tab-doc"><FileText className="w-3.5 h-3.5 mr-1" /> Document</TabsTrigger>
                  <TabsTrigger value="analysis" data-testid="mobile-tab-analysis"><ScanLine className="w-3.5 h-3.5 mr-1" /> Analyse</TabsTrigger>
                </TabsList>
                <TabsContent value="doc" className="mt-2">
                  {viewInvoice?.file_url ? (
                    <DocumentViewer src={viewInvoice.file_url} mime={viewInvoice.file_mime} name={viewInvoice.file_name || "Facture"} />
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-[13px] text-[#5E6875]">Aucun document.</div>
                  )}
                </TabsContent>
                <TabsContent value="analysis" className="mt-2">
                  <InvoiceAnalysis
                    invoice={viewInvoice}
                    products={products}
                    onLineSaved={async () => {
                      try {
                        const fresh = await api.getInvoice(viewInvoice.id);
                        if (fresh) setViewInvoice(fresh);
                      } catch { /* silent */ }
                      load();
                    }}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <DialogFooter className="sticky bottom-0 z-10 px-4 sm:px-5 py-3 border-t border-[#CDD7E3] bg-white flex-row justify-between gap-2 sm:justify-end flex-wrap">
            <Button
              variant="outline"
              onClick={() => askDelete(viewInvoice)}
              data-testid="invoice-preview-delete"
              className="border-[#F5C7C7] text-[#7C2A21] hover:bg-[#FDECEC] hover:border-[#D84343] gap-1.5"
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </Button>
            {/* Feb 2026 §« pas de reopen mutant statut au clic » — Le
                bouton "Modifier les données" est SUPPRIMÉ : chaque ligne
                du panneau Analyse a déjà son propre bouton Modifier qui
                PATCH la ligne en place (upsert history par line_id, aucun
                changement de statut, aucun risque de sortie de
                load_histories). L'édition ligne-par-ligne est la primitive
                canonique. Cf. Feb 2026 §« brouillon local jusqu'au save ». */}
            <Button variant="outline" onClick={() => setViewInvoice(null)} data-testid="invoice-preview-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation suppression — AlertDialog Radix (fiable sur mobile,
          contrairement à window.confirm bloqué par certaines webviews). */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && !deleting && setPendingDelete(null)}>
        <AlertDialogContent data-testid="invoice-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette facture ?</AlertDialogTitle>
            <AlertDialogDescription>
              La facture <span className="font-semibold text-[#0B1F33]">{pendingDelete?.invoice_number || "sans numéro"}</span>
              {pendingDelete?.supplier_name ? <> de <span className="font-semibold text-[#0B1F33]">{pendingDelete.supplier_name}</span></> : null}
              {" "}sera supprimée définitivement, ainsi que les entrées d'historique qu'elle a produites. Les fiches produits et recettes existantes sont conservées ; les prix seront recalculés à partir des factures restantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="invoice-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="invoice-delete-confirm-btn"
              className="bg-[#D84343] hover:bg-[#B93131] text-white"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              {deleting ? "Suppression…" : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* --------- Right-panel: read-only summary of extracted data --------- */
export function InvoiceAnalysis({ invoice, products = [], onLineSaved }) {
  const [editLine, setEditLine] = useState(null); // {line, index}
  if (!invoice) return null;
  const lines = invoice.lines || [];
  return (
    <div className="overflow-y-auto max-h-[75vh] p-4 space-y-3" data-testid="invoice-analysis-panel">
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <MetaKV k="Fournisseur" v={invoice.supplier_name || "—"} />
        <MetaKV k="N° facture" v={invoice.invoice_number || "—"} />
        <MetaKV k="Date" v={dateFR(invoice.invoice_date) || "—"} />
        <MetaKV k="Total HT" v={eur(invoice.total_ht)} mono />
      </div>

      {(invoice.warnings || []).length > 0 && (
        <div className="rounded-md border border-[#F5C7C7] bg-[#FDECEC] p-2.5">
          <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#7C2A21] flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" /> Contrôles
          </div>
          {invoice.warnings.map((w, i) => <div key={i} className="text-[12px] text-[#7C2A21]">• {w}</div>)}
        </div>
      )}

      <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] pt-1">
        Produits extraits · {lines.length}
      </div>
      <div className="space-y-1.5">
        {lines.map((l, i) => (
          <div key={i} className="rounded-md border border-[#CDD7E3] bg-white p-2.5" data-testid={`analysis-line-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#0B1F33] truncate">{l.product_name || l.raw_text || "—"}</div>
                {l.raw_text && l.raw_text !== l.product_name && (
                  <div className="text-[11px] font-mono text-[#5E6875] truncate">« {l.raw_text} »</div>
                )}
                <div className="text-[11px] text-[#5E6875] mt-0.5 flex flex-wrap gap-1.5">
                  {l.brand && <span className="font-semibold text-[#0B1F33]">{l.brand}</span>}
                  {l.content_value && l.content_unit && <span>{l.content_value} {l.content_unit}</span>}
                  {l.units_per_pack && <span>· pack de {l.units_per_pack}</span>}
                </div>
              </div>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                <div className="text-[13px] font-mono-num font-bold text-[#0B1F33]">{eur(l.line_total_ht)}</div>
                {/* Feb 2026 §« MEMES BONS PRIX PARTOUT » v3 — Utilise le
                    `price_reference` posé par la ligne source, PAS des
                    price_per_l/kg secondaires qui trompent la canette 33 cl
                    (qui a bien un price_per_l informatif mais dont la base
                    canonique est €/pièce). Fallback strict champ backend
                    seulement si `price_reference.base_unit === detected`. */}
                {(() => {
                  const pr = l.price_reference || {};
                  const pb = Number(pr.price_per_base) || 0;
                  const bu = pr.base_unit;
                  if (pb > 0 && bu) {
                    const lbl = bu === "u" ? "pièce" : bu;
                    const tw = Number(l.total_weight_g) || 0;
                    const tv = Number(l.total_volume_ml) || 0;
                    const tu = Number(l.total_units) || 0;
                    const qty = bu === "kg" && tw > 0 ? `${(tw/1000).toFixed(3)} kg`
                              : bu === "L" && tv > 0 ? `${(tv/1000).toFixed(3)} L`
                              : bu === "u" && tu > 0 ? `${tu} pièces` : null;
                    return (
                      <div className="text-[11px] text-[#5E6875] font-mono-num">
                        {eur(pb, 3)} €/{lbl}{qty ? ` · ${qty}` : ""}
                      </div>
                    );
                  }
                  return (
                    <div className="text-[11px] text-[#5E6875] font-mono-num">— à vérifier</div>
                  );
                })()}
                {/* Feb 2026 §« Modifier inline » — Bouton par ligne
                    ouvre le MÊME éditeur que le clic-point-courbe
                    (composant InvoiceLineEditDialog partagé). */}
                <button
                  type="button"
                  data-testid={`analysis-line-edit-${i}`}
                  onClick={() => setEditLine({ line: l, index: i })}
                  className="mt-1 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-widest text-[#B36B00] bg-[#FFF4D6] border border-[#F1D9A8] rounded px-2 py-0.5 hover:bg-[#FFE9B8]"
                >
                  <Pencil className="w-3 h-3" /> Modifier
                </button>
              </div>
            </div>
            {l.variation_pct != null && (
              <div className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold ${l.variation_pct > 0 ? "text-[#D84343]" : l.variation_pct < 0 ? "text-[#24845B]" : "text-[#5E6875]"}`}>
                {l.variation_pct > 0 ? "▲" : l.variation_pct < 0 ? "▼" : "•"} {l.variation_pct > 0 ? "+" : ""}{pct(l.variation_pct)} vs dernier prix
              </div>
            )}
          </div>
        ))}
      </div>
      <InvoiceLineEditDialog
        open={!!editLine}
        onOpenChange={(v) => !v && setEditLine(null)}
        invoiceId={invoice.id}
        lineIndex={editLine?.index}
        initialLine={editLine?.line}
        products={products}
        onSaved={(res) => {
          onLineSaved?.(res);
          try { window.dispatchEvent(new CustomEvent("rentascan:invoices-changed", { detail: { source: "line-edit", invoice_id: invoice.id } })); } catch {}
        }}
      />
    </div>
  );
}

function MetaKV({ k, v, mono }) {
  return (
    <div className="rounded-md border border-[#CDD7E3] bg-white p-2">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#5E6875]">{k}</div>
      <div className={`text-[13px] font-semibold text-[#0B1F33] truncate ${mono ? "font-mono-num" : ""}`}>{v}</div>
    </div>
  );
}

/* -------- Single line row with rich editing -------- */
function LineRow({ l, idx, products, selected, onSelect, onChange, onRemove }) {
  const meta = TIER_LABELS[l.status_tier] || TIER_LABELS.probable;
  const matched = products.find((p) => p.id === l.matched_product_id);
  const measurement = reviewMeasurement(l);
  return (
    <div
      onClick={onSelect}
      data-testid={`invoice-line-${idx}`}
      className={`px-4 py-4 border-b border-[#CDD7E3] last:border-b-0 cursor-pointer transition-colors ${selected ? "bg-[#DCEBFF]/40" : "hover:bg-[#F4F5EF]/50"}`}
      style={selected ? { borderLeft: `3px solid ${meta.color}` } : {}}
    >
      {/* Top row: raw text + interpreted name */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {l.raw_text && (
            <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875] mb-1">Texte facture</div>
          )}
          {l.raw_text && <div className="font-mono-num text-[12px] text-[#5E6875] mb-1 break-words">{l.raw_text}</div>}
          <Input value={l.product_name} onChange={(e) => onChange({ product_name: e.target.value })}
            className="!h-9 text-[14px] font-semibold" placeholder="Produit identifié" data-testid={`line-name-${idx}`} />
          {(l.brand || l.category) && (
            <div className="text-[11.5px] text-[#5E6875] mt-1 flex flex-wrap gap-2">
              {l.brand && <span className="font-semibold text-[#0B1F33]">{l.brand}</span>}
              {l.category && <span>· {l.category}</span>}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span className="pill" style={{ background: meta.color + "20", color: meta.color, border: `1px solid ${meta.border}` }}>
            {meta.label} · {Math.round((l.confidence || 0) * 100)}%
          </span>
          {Array.isArray(l.price_flags) && l.price_flags.length > 0 && (
            <span
              data-testid={`line-price-flag-${idx}`}
              title={l.price_flags.map((f) => f.message).join("\n")}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-widest bg-[#FFF4D6] text-[#B36B00] border border-[#F1D9A8]"
            >
              <AlertTriangle className="w-3 h-3" />
              Prix suspect
            </span>
          )}
          <button aria-label="Retirer ce produit" title="Retirer ce produit" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="text-[#D84343] hover:bg-[#FDECEC] p-2 rounded">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <InvoiceLinePricing line={l} index={idx} onChange={onChange} />

      {/* Product mapping */}
      <div className="mt-3 flex flex-wrap items-center gap-2 min-w-0">
        <span className="text-[11.5px] text-[#5E6875] shrink-0">Associer à :</span>
        <Select value={l.matched_product_id || "new"} onValueChange={(v) => onChange({
          matched_product_id: v === "new" ? null : v,
          // Manual pick from the dropdown = user vouches for this mapping.
          // Bump to confirmed so validate_invoice auto-merges it.
          ...(v !== "new" ? { status_tier: "confirmed", confidence: 1, user_confirmed: true, method: "manual" } : {}),
        })}>
          <SelectTrigger className="!h-10 text-[12px] flex-1 min-w-0 [&>span]:truncate"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">+ Nouveau produit</SelectItem>
            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {(() => {
          if (!matched) return null;
          const refP = Number(matched.ref_price_per_base || 0);
          const refB = matched.ref_base_unit;
          const current = refB === measurement.base ? measurement.price : null;
          if (!refP || current == null) return null;
          const varPct = ((current - refP) / refP) * 100;
          if (!Number.isFinite(varPct) || Math.abs(varPct) < 0.05) return null;
          const up = varPct > 5;
          return (
            <Badge
              data-testid={`line-variation-${idx}`}
              className={`${up ? "bg-[#FDECEC] text-[#7C2A21] border-[#F5C7C7]" : "bg-[#DCEBFF] text-[#0B1F33] border-[#DCEBFF]"} font-mono-num`}
            >
              {varPct > 0 ? "+" : ""}{pct(varPct)} vs dernier prix
            </Badge>
          );
        })()}
      </div>

    </div>
  );
}

/**
 * SortableTh — en-tête cliquable avec flèches contextuelles :
 *   • kind="text" → A→Z (asc) / Z→A (desc)
 *   • kind="num"  → croissant / décroissant
 * Une colonne inactive affiche l'icône neutre `ArrowUpDown`.
 */
function SortableTh({ label, k, kind = "text", align, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  let Icon = ArrowUpDown;
  if (active) {
    if (kind === "text") Icon = sortDir === "asc" ? ArrowUpAZ : ArrowDownAZ;
    else Icon = sortDir === "asc" ? ArrowUp : ArrowDown;
  }
  return (
    <th className={align === "num" ? "num" : ""}>
      <button
        type="button"
        data-testid={`invoice-sort-${k}`}
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 ${active ? "text-[#0B1F33]" : "text-[#5E6875]"} hover:text-[#0B1F33] font-inherit`}
        title={active ? (kind === "text" ? (sortDir === "asc" ? "Trier de Z à A" : "Trier de A à Z") : (sortDir === "asc" ? "Ordre décroissant" : "Ordre croissant")) : "Cliquer pour trier"}
      >
        <span>{label}</span>
        <Icon className={`w-3.5 h-3.5 ${active ? "" : "opacity-50"}`} />
      </button>
    </th>
  );
}
